import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import { useFeatures } from "../hooks/useFeatures";
import { useAdminAuth } from "../hooks/useAdminAuth";
import TabNavigation from "../components/TabNavigation";
import FeatureList from "../components/FeatureList";
import PriorityFilter from "../components/PriorityFilter";
import SortFilter from "../components/SortFilter";
import SuggestFeatureModal from "../components/SuggestFeatureModal";
import { STATUS_TABS, statusMatchesTab, getTabLabel } from "../lib/statusConfig";
import { getEffectiveTime } from "../lib/dateUtils";

const { FiLock, FiMail, FiSearch, FiLogOut, FiPlus } = FiIcons;

const AdminPage = () => {
  const { isLoggedIn, session, isAdmin, checkingSession, checkingAdminStatus, sessionError, signIn, signOut } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const {
    features,
    loading,
    error,
    createFeature,
    updateFeature,
    closeFeature,
    reopenFeature,
    releaseFeature,
    deleteFeature,
    upvoteFeature,
    getInternalNote,
    saveInternalNote,
  } = useFeatures(!checkingSession && !!session); // Wait for the session restore to finish, and only ever pass true once a session actually exists — never hardcoded, and never before Supabase has attached the auth token. Otherwise, on a hard refresh, this fires an authenticated-only query (feature_requester_contact) before the session exists, which can surface as a spurious "Unable to load features" error.
  //
  // Deliberately gated on `session`, not on the confirmed `isAdmin` result:
  // gating on `isAdmin` forced this fetch to wait for the is_admin() RPC to
  // finish before even starting the (separate) features/contacts request,
  // serializing two round trips back-to-back and roughly doubling
  // time-to-render. Once a session exists, the JWT is already attached to
  // every request, so this can safely run in parallel with the admin
  // check instead of after it — RLS on feature_requester_contact still
  // independently rejects the read if the session turns out not to be an
  // admin, so this loses no security, just the artificial wait.

  const [activeTab, setActiveTab] = useState("all");
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [sortOrder, setSortOrder] = useState("newest"); // default: newest on top, on every tab, on load
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [actionError, setActionError] = useState("");
  const [loggingOut, setLoggingOut] = useState(false);

  // signOut() is async and does real work after clearing React state — most
  // importantly, purging the persisted Supabase session from localStorage.
  // Fire-and-forget (`onClick={() => { signOut(); }}`) meant that if the user
  // closed the tab the instant the login screen appeared, that purge never
  // ran and the stored session survived, so returning to /admin logged them
  // straight back in. Awaiting it, and disabling the button meanwhile, keeps
  // the tab alive long enough for the sign-out to actually complete.
  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await signOut();
    } finally {
      setLoggingOut(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      await signIn(email, password);
    } catch (err) {
      setLoginError(err.message || "Failed to sign in. Please check your credentials.");
    } finally {
      setLoggingIn(false);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), 5000);
  };

  const runAction = async (fn, successText) => {
    setActionError("");
    try {
      const result = await fn();
      if (successText) showSuccess(successText);
      return result;
    } catch (err) {
      console.error(err);
      setActionError(err.message || "Something went wrong. Please try again.");
      // Re-throw so callers (e.g. the Close/Release modals) know the
      // action actually failed, instead of assuming success and closing
      // themselves while the database was never updated.
      throw err;
    }
  };

  const handleUpdate = (id, updates) =>
    runAction(() => updateFeature(id, updates), "Feature updated.");

  const handleClose = (id, comment) =>
    runAction(() => closeFeature(id, comment), "Request closed.");

  const handleRelease = (id, note) =>
    runAction(() => releaseFeature(id, note), "Marked as released.");

  const handleReopen = (id) =>
    runAction(() => reopenFeature(id), "Request reopened.");

  const handleDelete = (id) =>
    runAction(() => deleteFeature(id), "Feature deleted.");

  const handleAddFeature = async (featureData) => {
    // Only close the modal if the add actually succeeded — runAction now
    // throws on failure, so a rejected create leaves the modal open with
    // its own error shown, instead of silently closing on a failed write.
    await runAction(() => createFeature(featureData), `"${featureData.feature}" has been added to the roadmap!`);
    setShowAddModal(false);
  };

  // Chronological sort, direction controlled by sortOrder ("newest" first by
  // default). Sorts by updated_at, falling back to created_at only when
  // updated_at is missing/invalid. The card display (FeatureCard) uses this
  // exact same helper, so the date you see always matches what determined
  // the item's position in the list.
  const sortedFeatures = useMemo(() => {
    const direction = sortOrder === "oldest" ? -1 : 1;
    return [...features].sort((a, b) => direction * (getEffectiveTime(b) - getEffectiveTime(a)));
  }, [features, sortOrder]);

  const searchedFeatures = useMemo(() => {
    if (!searchQuery.trim()) return sortedFeatures;
    const q = searchQuery.toLowerCase();
    const scored = sortedFeatures
      .map((f) => {
        const title = f.feature?.toLowerCase() || "";
        const desc = f.description?.toLowerCase() || "";
        const cat = f.category?.toLowerCase() || "";
        const discord = f.discord_username?.toLowerCase() || "";
        const email = f.email?.toLowerCase() || "";
        let score = 0;
        if (title === q) score = 4;
        else if (title.startsWith(q)) score = 3;
        else if (title.includes(q)) score = 2;
        else if (desc.includes(q) || cat.includes(q) || discord.includes(q) || email.includes(q)) score = 1;
        return { ...f, _score: score };
      })
      .filter((f) => f._score > 0)
      .sort((a, b) => b._score - a._score);
    return scored;
  }, [sortedFeatures, searchQuery]);

  const tabs = useMemo(() => {
    let filteredFeatures = searchedFeatures;
    if (selectedPriorities.length > 0) {
      filteredFeatures = filteredFeatures.filter((f) =>
        selectedPriorities.includes(f.priority?.toLowerCase() || "medium")
      );
    }
    return STATUS_TABS.map((tab) => ({
      id: tab.id,
      label: tab.label,
      count: filteredFeatures.filter((f) => statusMatchesTab(f.status, tab.id)).length,
    }));
  }, [searchedFeatures, selectedPriorities]);

  const currentFeatures = useMemo(() => {
    let filtered = searchedFeatures;
    if (selectedPriorities.length > 0) {
      filtered = filtered.filter((f) =>
        selectedPriorities.includes(f.priority?.toLowerCase() || "medium")
      );
    }
    if (activeTab !== "all") {
      filtered = filtered.filter((f) => statusMatchesTab(f.status, activeTab));
    }
    return filtered;
  }, [searchedFeatures, activeTab, selectedPriorities]);

  // Empty-state copy. The "All" tab takes no adjective ("No features yet"),
  // and the other tabs use their display label rather than the raw tab id.
  const emptyMessage = useMemo(() => {
    if (features.length === 0) return "No features found.";
    const scope = activeTab === "all" ? "" : `${getTabLabel(activeTab).toLowerCase()} `;
    return selectedPriorities.length > 0
      ? `No ${scope}features match the selected priority filters`
      : `No ${scope}features yet`;
  }, [features.length, activeTab, selectedPriorities.length]);

  // Still checking for an existing Supabase Auth session, OR we found a
  // session and are waiting on the is_admin() RPC to confirm admin status.
  // The second case matters: without it, a valid session with a
  // still-in-flight admin check would make isLoggedIn briefly false and
  // flash the login form before flipping back to the real content.
  if (checkingSession || (session && checkingAdminStatus)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0d0d10" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500 mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg">Checking session...</p>
        </div>
      </div>
    );
  }

  // No fallback: if Supabase isn't configured, there is nothing a login
  // attempt can do — show this clearly instead of a login form that will
  // just fail.
  if (sessionError && !isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0d0d10" }}>
        <div className="relative z-10 w-full max-w-sm mx-4 p-8 rounded-2xl text-center"
          style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)" }}
        >
          <h2 className="text-lg font-semibold text-red-300 mb-2">Unable to reach the database</h2>
          <p className="text-sm text-red-400/80 leading-relaxed">{sessionError}</p>
        </div>
      </div>
    );
  }

  // Login screen — real Supabase Auth (email + password), no client-side
  // admin password. A successful sign-in still only grants access if the
  // account is also listed in `admin_users` (enforced by useAdminAuth + RLS).
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0d0d10" }}>
        <div className="fixed left-[-200px] top-[-200px] w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(109,40,217,0.18) 0%, transparent 70%)", filter: "blur(40px)" }}
        />
        <div className="fixed right-[-200px] bottom-[-100px] w-[700px] h-[700px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(124,58,237,0.20) 0%, transparent 70%)", filter: "blur(60px)" }}
        />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-sm mx-4 p-8 rounded-2xl"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <div className="flex flex-col items-center mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
              style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)", boxShadow: "0 0 24px rgba(124,58,237,0.40)" }}
            >
              <SafeIcon icon={FiLock} className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">Admin Login</h2>
            <p className="text-sm text-gray-500 mt-1">Sign in with your admin account</p>
          </div>

          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-500" />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="username"
                placeholder="Email"
                className="block w-full pl-10 pr-3 py-2.5 rounded-lg text-sm text-white placeholder-gray-500 outline-none focus:ring-1 focus:ring-violet-500 transition-all"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)" }}
              />
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiLock} className="w-4 h-4 text-gray-500" />
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="Password"
                className="block w-full pl-10 pr-3 py-2.5 rounded-lg text-sm text-white placeholder-gray-500 outline-none focus:ring-1 focus:ring-violet-500 transition-all"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)" }}
              />
            </div>

            {loginError && (
              <div
                className="px-3 py-2.5 rounded-lg text-sm text-red-300"
                style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.20)" }}
              >
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loggingIn}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all active:scale-[0.97] flex items-center justify-center disabled:opacity-50"
              style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)", boxShadow: "0 0 20px rgba(124,58,237,0.30)" }}
            >
              {loggingIn ? "Signing in…" : "Login"}
            </button>

            <p className="text-xs text-gray-600 text-center leading-relaxed mt-1">
              Admin accounts are provisioned by an existing admin via Supabase Auth —
              there's no self-signup here.
            </p>
          </form>
        </motion.div>
      </div>
    );
  }

  // Admin dashboard
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0d0d10" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500 mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg">Loading features...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "#0d0d10" }}>
      <div className="fixed left-[-200px] top-[-200px] w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(109,40,217,0.18) 0%, transparent 70%)", filter: "blur(40px)" }}
      />
      <div className="fixed right-[-200px] bottom-[-100px] w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.20) 0%, transparent 70%)", filter: "blur(60px)" }}
      />
      <div className="fixed left-1/2 bottom-0 -translate-x-1/2 w-[900px] h-[300px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center bottom, rgba(124,58,237,0.12) 0%, transparent 70%)", filter: "blur(30px)" }}
      />

      <div className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="w-full max-w-[1137px] h-auto opacity-100 flex flex-col gap-3 mb-6 px-4 sm:px-0 relative">
            {/* Logout button */}
            <div className="absolute top-0 right-0">
              <button
                onClick={handleLogout}
                disabled={loggingOut}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 transition-colors px-3 py-1.5 rounded-lg disabled:opacity-50"
                style={{ border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                Logout
              </button>
            </div>

            <div className="flex flex-col items-center pt-8 sm:pt-0">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="w-full h-auto opacity-100"
              >
                <h1 className="font-figtree font-bold text-2xl sm:text-3xl md:text-[40px] leading-tight md:leading-[48px] tracking-tight text-center">
                  <span className="text-white">Greta LTD </span>
                  <span className="bg-gradient-to-r from-[#CB6DDC] via-[#7E40F1] to-[#4685FA] text-transparent bg-clip-text">Roadmap</span>
                </h1>
              </motion.div>

              <p className="font-figtree font-normal text-sm sm:text-base md:text-[16px] leading-normal md:leading-[24px] tracking-normal text-center text-gray-400 mt-2 max-w-[90%] md:max-w-full">
                Stay updated on the latest feature developments from ideas to completed milestones.
              </p>
            </div>

            <div className="w-full h-px mt-2" style={{ background: "rgba(255,255,255,0.07)" }}></div>
          </div>

          {/* Admin Mode Banner */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 mb-6 px-4 py-3 rounded-xl text-sm"
            style={{ background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.30)" }}
          >
            <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
            <span className="text-violet-300 font-medium">
              Admin Mode{session?.user?.email ? ` — signed in as ${session.user.email}` : ""} — you can edit, close, release, delete, and change the status of any request
            </span>
          </motion.div>

          {/* Success Message */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl text-sm text-green-300"
              style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.20)" }}
            >
              {successMessage}
            </motion.div>
          )}

          {/* Action Error */}
          {actionError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl text-sm text-red-300"
              style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.20)" }}
            >
              {actionError}
            </motion.div>
          )}

          {/* Error Banner (connection) */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl"
              style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.20)" }}
            >
              <h3 className="font-medium text-red-400 mb-1 text-sm">Connection Error</h3>
              <p className="text-red-400/70 text-xs">{error}</p>
            </motion.div>
          )}

          {/* Search Bar */}
          <div className="flex items-center gap-2 mb-6 max-w-xl">
            <div className="relative flex-1">
              <SafeIcon
                icon={FiSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && setSearchQuery("")}
                placeholder="Search by name, description, category, Discord, or email..."
                className="w-full pl-10 pr-10 py-2.5 rounded-lg text-sm text-white placeholder-gray-500 outline-none transition-all focus:ring-1 focus:ring-violet-500"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)" }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 text-xs font-medium"
                >
                  ✕
                </button>
              )}
            </div>
            <button
              onClick={() => setActiveTab("all")}
              className="flex items-center gap-1.5 px-4 py-2.5 active:scale-[0.97] text-white text-sm font-medium rounded-lg transition-all whitespace-nowrap"
              style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)", boxShadow: "0 0 20px rgba(124,58,237,0.30)" }}
            >
              <SafeIcon icon={FiSearch} className="w-3.5 h-3.5" />
              Search
            </button>
          </div>

          {/* Navigation Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
            <div className="flex-1 max-w-full lg:max-w-4xl">
              <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} tabs={tabs} />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-4 sm:mt-0">
              <div className="w-full sm:w-auto sm:flex-shrink-0">
                <SortFilter sortOrder={sortOrder} onSortChange={setSortOrder} />
              </div>
              <div className="w-full sm:w-auto sm:flex-shrink-0">
                <PriorityFilter
                  selectedPriorities={selectedPriorities}
                  onPriorityChange={setSelectedPriorities}
                  onClearFilters={() => setSelectedPriorities([])}
                />
              </div>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 sm:py-1.5 text-white rounded-lg transition-all active:scale-[0.97] shadow-lg w-full sm:w-auto whitespace-nowrap"
                style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)", boxShadow: "0 0 20px rgba(124,58,237,0.25)" }}
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span className="text-sm">Add Feature</span>
              </button>
            </div>
          </div>

          {/* Feature List with admin controls */}
          <motion.div
            key={`${activeTab}-${selectedPriorities.join("-")}-${sortOrder}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
          >
            <FeatureList
              features={currentFeatures}
              onUpvote={upvoteFeature}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onClose={handleClose}
              onRelease={handleRelease}
              onReopen={handleReopen}
              onLoadInternalNote={getInternalNote}
              onSaveInternalNote={saveInternalNote}
              isAdmin={true}
              emptyMessage={emptyMessage}
            />
          </motion.div>

          <SuggestFeatureModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSubmit={handleAddFeature}
            isAdmin
          />
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
